/**
 * `.rl-banner.rl-banner-warn` — the amber band over the outcome list, for either
 * way a reel can be wrong (CAE-1552).
 *
 * Both sentences are derived from `reelStats`, which the rail's state dot and
 * `readiness.ts`'s Setup Rail step read too: one derivation, so the banner
 * cannot raise an alarm the step above it has not — nor stay quiet about one it
 * has, which is how the too-few-enabled case went missing until review.
 *
 * > **No "Bulk upload" button.** The handoff draws an outline button on the
 * > right of this banner. There is nothing behind it: `PATCH …/options/:id/asset`
 * > attaches one asset to one Reel Option, and `assetsApi.upload` takes one file
 * > — a real bulk flow would be a picker that maps N library assets onto the N
 * > options missing them, in order, which is a screen of its own and closer to
 * > CAE-1553's work than to this. A button that opens the same one-at-a-time
 * > picker under the word "bulk" is worse than no button, so the sentence does
 * > the work instead and says exactly where to click. Raised on CAE-1552.
 */
import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';
import { WarnIcon } from '../../../../components/ui';

export interface ReelWarningBannerProps {
  /** The claim, in a sentence — "3 enabled options are missing assets." */
  headline: string;
  /** What to do about it. */
  detail: string;
}

export const ReelWarningBanner = ({ headline, detail }: ReelWarningBannerProps) => {
  return (
    <Box
      sx={(theme: Theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 14px',
        borderRadius: `${theme.tokens.radius.insetRow}px`,
        backgroundColor: theme.tokens.color.warnSoft,
        border: `${theme.tokens.border.width} solid ${theme.tokens.componentColor.marketBadgeEmptyBorder}`,
        color: theme.tokens.componentColor.marketBadgeEmptyText,
        fontSize: '13px',
        lineHeight: 1.45,
      })}
    >
      <Box
        aria-hidden
        sx={(theme: Theme) => ({
          width: '24px',
          height: '24px',
          flexShrink: 0,
          borderRadius: `${theme.tokens.radius.idChip}px`,
          display: 'grid',
          placeItems: 'center',
          backgroundColor: theme.tokens.componentColor.marketBadgeEmptyBorder,
          color: theme.tokens.componentColor.marketBadgeEmptyText,
        })}
      >
        <WarnIcon />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/*
         * The whole claim in one element rather than split across the bold and
         * the advice: a sentence broken over two nodes is one no screen reader
         * reads as a sentence, and no test can match as one either.
         */}
        <Box component="strong" sx={{ fontWeight: 600 }}>
          {headline}
        </Box>{' '}
        <Box component="span">{detail}</Box>
      </Box>
    </Box>
  );
};
